import { jsx, jsxs } from "react/jsx-runtime";
import { string, bool, func, oneOfType, element } from "prop-types";
import React, { forwardRef, useContext } from "react";
import Label from "@ingka/label";
import cx from "classnames";
import { FormFieldContext } from "@ingka/form-field";
const propTypes = {
  /** Unique ID of the checkbox */
  id: string.isRequired,
  /** Name attribute of the checkbox */
  name: string,
  /** Value attribute of the checkbox */
  value: string.isRequired,
  /** Text that is passed to the Label component */
  label: oneOfType([string, element]),
  /** Text that is rendered as caption */
  caption: oneOfType([string, element]),
  /** Function that is triggered on state change of the checkbox */
  onChange: func,
  /** Defines disabled state of the checkbox */
  disabled: bool,
  /** Default checked value for the component, makes component Uncontrolled */
  defaultChecked: bool,
  /** Checked value for the component, makes component Controlled */
  checked: bool,
  /** Sets aria-describedby to reference label */
  describedById: string,
  /** Prefix all classes in every element with this string */
  prefix: string,
  /** Can be used to add classes to base HTML element. */
  className: string,
  /** Sets `required` attribute for `<input>` element */
  required: bool,
  /** Enables subtle checkbox styling */
  subtle: bool,
  /** Sets embedded checkbox's indeterminate state (if set) */
  indeterminate: bool
};
const Checkbox = forwardRef((props, publicRef) => {
  const {
    className,
    prefix = "",
    id,
    label,
    caption,
    name = "checkboxname",
    value,
    onChange,
    disabled = false,
    describedById,
    required,
    subtle = false,
    indeterminate,
    ...other
  } = props;
  const classes = cx(
    subtle && `${prefix}checkbox--subtle`,
    `${prefix}checkbox`,
    className
  );
  const { shouldValidate, valid } = useContext(FormFieldContext);
  const checkboxRef = publicRef || React.useRef(null);
  React.useEffect(() => {
    if (typeof indeterminate !== "undefined" && checkboxRef.current) {
      checkboxRef.current.indeterminate = Boolean(indeterminate);
    }
  }, [indeterminate]);
  return /* @__PURE__ */ jsxs("span", { className: classes, children: [
    /* @__PURE__ */ jsx(
      "input",
      {
        ref: checkboxRef,
        type: "checkbox",
        value,
        name,
        id,
        disabled,
        onChange,
        "aria-describedby": describedById,
        required,
        "aria-invalid": shouldValidate ? !valid : null,
        ...other
      }
    ),
    /* @__PURE__ */ jsx("span", { className: `${prefix}checkbox__symbol` }),
    label && /* @__PURE__ */ jsxs("span", { className: `${prefix}checkbox__label`, children: [
      /* @__PURE__ */ jsx(Label, { prefix, htmlFor: id, label }),
      caption && /* @__PURE__ */ jsx("span", { className: `${prefix}checkbox__caption`, children: /* @__PURE__ */ jsx(Label, { prefix, htmlFor: id, label: caption }) })
    ] })
  ] });
});
Checkbox.propTypes = propTypes;
Checkbox.displayName = "Checkbox";
var Checkbox_default = Checkbox;
export {
  Checkbox,
  Checkbox_default as default,
  propTypes
};
